import gsap from 'gsap'
import type { FC } from 'react'
import { useEffect, useMemo, useRef } from 'react'

export enum NotificationTypes {
  error = 'error',
  success = 'success',
  warning = 'warning',
  info = 'info',
}

export interface NotificationArg {
  message: string
  link?: { title: string; url: string }
  type: NotificationTypes
}
export interface ClearNotificationArg extends NotificationArg {
  id: string
}

const getIcon: FC<{ type: NotificationTypes }> = ({ type }) => {
  if (type === NotificationTypes.success)
    return (
      <svg
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="24" y="28" width="24" height="24" rx="6" fill="#3CB658" />
        <g filter="url(#filter0_f_6415_6614)">
          <rect
            x="18"
            y="22"
            width="36"
            height="36"
            rx="18"
            fill="#3CB658"
            fillOpacity="0.32"
          />
        </g>
        <g clipPath="url(#clip0_6415_6614)">
          <path
            d="M41.8805 34.0312L33.7283 41.5941L30.2806 37.9149L28 40.052L33.5698 45.996L44 36.3287L41.8805 34.0312Z"
            fill="white"
          />
        </g>
        <defs>
          <filter
            id="filter0_f_6415_6614"
            x="-12"
            y="-8"
            width="96"
            height="96"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="15"
              result="effect1_foregroundBlur_6415_6614"
            />
          </filter>
          <clipPath id="clip0_6415_6614">
            <rect
              width="16"
              height="16"
              fill="white"
              transform="translate(28 32)"
            />
          </clipPath>
        </defs>
      </svg>
    )

  if (type === NotificationTypes.info)
    return (
      <svg
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="24" y="28" width="24" height="24" rx="6" fill="#3F7AE3" />
        <g filter="url(#filter0_f_6415_4699)">
          <rect
            x="18"
            y="22"
            width="36"
            height="36"
            rx="18"
            fill="#3F7FD7"
            fillOpacity="0.32"
          />
        </g>
        <path
          d="M38.803 45.0702C38.533 45.1752 37.678 45.4252 37.288 45.2027C37.2342 45.1715 37.1888 45.1276 37.1557 45.0749C37.1226 45.0222 37.1028 44.9623 37.098 44.9002C36.9755 44.1202 37.558 42.3177 37.873 41.3502C37.978 41.0277 38.053 40.7927 38.083 40.6502C38.3105 39.5977 38.198 38.7927 37.748 38.2577C37.2066 37.6141 36.246 37.4888 35.458 37.6052C34.6568 37.712 33.8738 37.9273 33.1305 38.2452C33.096 38.2619 33.0658 38.2864 33.0424 38.3168C33.019 38.3472 33.0029 38.3826 32.9955 38.4202L32.833 39.1977C32.8243 39.2415 32.8273 39.2867 32.8418 39.3289C32.8563 39.3711 32.8817 39.4086 32.9155 39.4377C32.9498 39.4662 32.9908 39.4853 33.0346 39.4932C33.0784 39.501 33.1235 39.4974 33.1655 39.4827C33.463 39.3727 34.393 39.1252 34.7255 39.4302C34.913 39.6027 34.8605 39.9677 34.7856 40.2402C34.7081 40.5252 34.6155 40.8327 34.5206 41.1552C33.9056 43.2477 33.2056 45.6202 34.3156 46.3602C34.8407 46.7612 35.4799 46.9845 36.1406 46.9977C36.7906 46.9977 37.5931 46.7927 38.7481 46.3252C38.7829 46.3117 38.8142 46.2905 38.8397 46.2632C38.8652 46.236 38.8843 46.2033 38.8956 46.1677L39.1331 45.3752C39.1465 45.33 39.1471 45.282 39.1347 45.2365C39.1223 45.191 39.0975 45.1499 39.0631 45.1177C39.0286 45.0861 38.9859 45.0649 38.9399 45.0565C38.8939 45.0481 38.8465 45.0529 38.803 45.0702Z"
          fill="white"
        />
        <path
          d="M37.3544 33C36.9967 33.0002 36.6472 33.1065 36.35 33.3054C36.0527 33.5042 35.8211 33.7867 35.6843 34.1171C35.5476 34.4476 35.5119 34.8112 35.5817 35.1619C35.6515 35.5127 35.8238 35.8348 36.0766 36.0877C36.3295 36.3406 36.6517 36.5129 37.0025 36.5827C37.3532 36.6525 37.7168 36.6168 38.0472 36.4801C38.3777 36.3433 38.6602 36.1117 38.859 35.8144C39.0579 35.5172 39.1641 35.1676 39.1644 34.81C39.1642 34.33 38.9735 33.8697 38.6341 33.5303C38.2947 33.1909 37.8344 33.0002 37.3544 33Z"
          fill="white"
        />
        <defs>
          <filter
            id="filter0_f_6415_4699"
            x="-12"
            y="-8"
            width="96"
            height="96"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="15"
              result="effect1_foregroundBlur_6415_4699"
            />
          </filter>
        </defs>
      </svg>
    )

  if (type === NotificationTypes.warning)
    return (
      <svg
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="24" y="28" width="24" height="24" rx="6" fill="#F4BA35" />
        <g filter="url(#filter0_f_6415_3628)">
          <rect
            x="18"
            y="22"
            width="36"
            height="36"
            rx="18"
            fill="#E3BA39"
            fillOpacity="0.32"
          />
        </g>
        <path
          d="M37.3749 42.4874L37.8814 32.75H34.25L34.7565 42.4874H37.3749Z"
          fill="white"
        />
        <path
          d="M37.5361 46.2769C37.8714 45.4681 37.4875 44.5406 36.6787 44.2053C35.8698 43.8701 34.9423 44.254 34.6071 45.0628C34.2718 45.8716 34.6557 46.7991 35.4645 47.1344C36.2734 47.4697 37.2009 47.0858 37.5361 46.2769Z"
          fill="white"
        />
        <defs>
          <filter
            id="filter0_f_6415_3628"
            x="-12"
            y="-8"
            width="96"
            height="96"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="15"
              result="effect1_foregroundBlur_6415_3628"
            />
          </filter>
        </defs>
      </svg>
    )

  if (type === NotificationTypes.error)
    return (
      <svg
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="24" y="28" width="24" height="24" rx="6" fill="#D63031" />
        <g filter="url(#filter0_f_6415_5286)">
          <rect
            x="18"
            y="22"
            width="36"
            height="36"
            rx="18"
            fill="#D63031"
            fillOpacity="0.32"
          />
        </g>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M33.8768 39.9982L29.9375 36.0588L32.0588 33.9375L35.9982 37.8768L39.9375 33.9375L42.0588 36.0588L38.1195 39.9982L42.0588 43.9375L39.9375 46.0588L35.9982 42.1195L32.0588 46.0588L29.9375 43.9375L33.8768 39.9982Z"
          fill="white"
        />
        <defs>
          <filter
            id="filter0_f_6415_5286"
            x="-12"
            y="-8"
            width="96"
            height="96"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="15"
              result="effect1_foregroundBlur_6415_5286"
            />
          </filter>
        </defs>
      </svg>
    )

  return <div></div>
}

const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.23938 11.1453C0.848851 11.5358 0.848851 12.169 1.23938 12.5595L1.33414 12.6542C1.72467 13.0448 2.35783 13.0448 2.74836 12.6542L6.94438 8.45822L11.1414 12.6552C11.5319 13.0457 12.165 13.0457 12.5556 12.6552L12.6503 12.5604C13.0409 12.1699 13.0409 11.5367 12.6503 11.1462L8.45336 6.94924L12.6515 2.75109C13.042 2.36057 13.042 1.7274 12.6515 1.33688L12.5567 1.24211C12.1662 0.851588 11.5331 0.851588 11.1425 1.24211L6.94438 5.44026L2.74719 1.24307C2.35666 0.852546 1.7235 0.852546 1.33297 1.24307L1.23821 1.33784C0.847682 1.72836 0.847681 2.36153 1.23821 2.75205L5.4354 6.94924L1.23938 11.1453Z"
      fill="#3F4756"
    />
  </svg>
)

const titles: Record<NotificationTypes, string> = {
  error: 'Error',
  success: 'Success',
  warning: 'Warning',
  info: 'Info',
}

interface Props {
  notification: ClearNotificationArg
  onClose: () => void
  duration: number
  slideDuration: number
  expendingDuration: number
  timeoutDelay: number
}

export const NotificationCard = (props: Props) => {
  // --------------------- ===
  //  PROPS
  // ---------------------
  const { notification, onClose, duration, timeoutDelay } = props

  const { id, message, link, type } = notification

  // --------------------- ===
  //  REF
  // ---------------------
  const cardRef = useRef(null)
  const barRef = useRef(null)
  const infoRef = useRef<HTMLDivElement>(null)
  const infoContentRef = useRef<HTMLDivElement>(null)
  const tl = useRef(gsap.timeline())
  const didMount = useRef(false) // strict mode

  // --------------------- ===
  //  HELPERS
  // ---------------------
  const buildTl = () => {
    // tl.current.to(
    //   null,
    //   {
    //     duration: 0,
    //     onComplete: () => {
    //       infoRef.current?.classList.remove('hidden')
    //     },
    //   },
    //   '<' + props.slideDuration
    // )
    // tl.current.to(
    //   infoRef.current,
    //   {
    //     duration: 0.2,
    //     width: 256,
    //     ease: 'none',
    //     onComplete: () => {
    //       infoContentRef.current?.classList.remove('hidden')
    //     },
    //   },
    //   '<'
    // )

    tl.current.to(
      cardRef.current,
      {
        duration: props.expendingDuration,
        width: 364,
        // onComplete: () => {
        //   infoRef.current?.classList.remove('hidden')
        // },
      },
      '<' + props.slideDuration
    )

    tl.current.to(barRef.current, {
      duration: timeoutDelay,
      width: '100%',
      ease: 'none',
      onComplete: onClose,
    })

    tl.current.to(cardRef.current, {
      duration: props.expendingDuration,
      width: 80,
      // onComplete: () => {
      //   infoRef.current?.classList.remove('hidden')
      // },
    })
  }

  // --------------------- ===
  //  EFFECTS
  // ---------------------
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true

      gsap.set(cardRef.current, { display: 'flex' }) // Keeps card from flashing
      gsap.from(cardRef.current, {
        height: 0,
        marginBottom: 0,
        padding: 0,
        duration,
        ease: 'none',
        overwrite: false,
      })
      gsap.from(cardRef.current, {
        duration: duration * 2,
        delay: duration / 2,
        yPercent: 150,
        ease: 'elastic.out(1, 1)',
        overwrite: false,
      })

      // gsap.set(infoRef.current, {
      //   width: 0,
      // })

      // infoContentRef.current?.classList.add('hidden')

      gsap.set(cardRef.current, {
        width: 80,
        height: 80,
      })

      buildTl()
    }
  }, [])

  // --------------------- ===
  //  RENDER
  // ---------------------
  const Icon = useMemo(() => getIcon({ type }), [type])

  return (
    <div
      key={id}
      id={id}
      ref={cardRef}
      style={{
        boxShadow: '0px 8px 16px 0px #00000075',
      }}
      className=" relative overflow-hidden w-fit mb-4 0px-6 max-w-full bg-[#17191B] rounded-xl border border-[#2E3343]"
      onMouseEnter={() => {
        tl.current.pause()
      }}
      onMouseLeave={() => {
        tl.current.play()
      }}
    >
      <div
        ref={barRef}
        className="z-0 absolute inset-0 h-full w-0 bg-[#2F3036] bg-opacity-25 pointer-events-none"
      ></div>
      <div className="z-10 flex items-center 0gap-4 ">
        <div className="0-mx-5 min-w-20 w-20">{Icon}</div>
        <div ref={infoRef} className="0hidden w-[256px] py-5 h-full">
          <div
            ref={infoContentRef}
            className="0hidden h-full flex items-center gap-4  "
          >
            <div className="grow flex flex-col gap-1">
              <div className="text-white font-semibold leading-5">
                {titles[notification.type]}
              </div>
              <div className="min-w-[209px] max-w-[209px] text-kap-new-secondary text-sm leading-4">
                {message}
              </div>
              {link && (
                <a
                  className="underline text-white"
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {link.title}
                </a>
              )}
            </div>
            <div className="w-[1px] min-w-[1px] h-full bg-[#2E3343]"></div>
            <div className="min-w-[14px] mr-6 cursor-pointer" onClick={onClose}>
              <CloseIcon />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
